import {
  // reason: this is the wrapped component which we will allow since this is the exported version
  // eslint-disable-next-line @dhl/dhl-ui/no-restricted-imports
  CircularProgress,
  LinearProgress,
  type CircularProgressProps,
} from '@mui/material'
import { spacing } from '../../theme/spacing'
import { Box } from '../Box'
import { StackRow } from '../Stack'
import { Caption1, Typography } from '../Typography'

export type ProgressProps = {
  /**
   * @default 'primary'
   */
  color?:
    | 'primary'
    | 'secondary'
    | 'success'
    | 'info'
    // only allowed inside buttons etc
    | 'inherit'

  'aria-label'?: string
} & Pick<
  CircularProgressProps,
  /**
   * @default 'indeterminate'
   */
  'variant' | 'sx'
> &
  (
    | {
        shape: 'linear'
        value: number
        variant: 'determinate'
        size?: never
      }
    | {
        shape: 'linear'
        variant?: 'indeterminate'
        value?: never
        size?: never
      }
    | {
        /**
         * Shape is circular
         * @default 'circular'
         */
        shape?: 'circular'
        variant: 'determinate'
        value: number

        /**
         * Size for our circular determinate version
         * @default 'M'
         */
        size?: 'S' | 'M' | 'L' | 'XL'
      }
    | {
        /**
         * Shape is circular
         * @default 'circular'
         */
        shape?: 'circular'
        variant?: 'indeterminate'
        value?: never

        /**
         * Diameter in pixels
         * @default 16
         */
        size?: number
      }
  )

const determinateSizes = {
  S: 6,
  M: 8,
  L: 10,
  XL: 14,
}

const determinateLabelVariants = {
  S: 'caption1',
  M: 'body2',
  L: 'body1',
  XL: 'body1',
} as const

/**
 * Progress
 *
 * Demos:
 * - [Progress](https://design-system.accept.cl1.intra.dhlparcel.io/?path=/docs/primitives-progress--docs)
 *
 */

export const Progress = (props: ProgressProps) => {
  const { sx, color = 'primary', variant: variantProp, value, shape, size: sizeProp, 'aria-label': ariaLabel } = props
  const variant = variantProp ?? 'indeterminate'
  const isDeterminate = variant === 'determinate'

  if (shape !== 'linear') {
    const size = variantProp === 'determinate' ? spacing(determinateSizes[sizeProp ?? 'M']) : (sizeProp ?? 16)

    return (
      <Box sx={{ position: 'relative', display: 'inline-flex', width: size }}>
        <CircularProgress
          color={color}
          size={size}
          variant={variant}
          value={value}
          data-chromatic="ignore"
          aria-label={ariaLabel ?? 'loading'}
          sx={sx}
          enableTrackSlot={isDeterminate}
        />

        {variantProp === 'determinate' && (
          <Box
            sx={{
              top: 0,
              left: 0,
              bottom: 0,
              right: 0,
              position: 'absolute',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant={determinateLabelVariants[sizeProp ?? 'M']} component="div">
              {value ? Math.round(value) : ''}%
            </Typography>
          </Box>
        )}
      </Box>
    )
  }

  return (
    <StackRow>
      <Box sx={{ flex: 1 }}>
        <LinearProgress color={color} variant={variant} value={value} aria-label={ariaLabel ?? 'Loading'} />
      </Box>
      {isDeterminate && <Caption1>{value}%</Caption1>}
    </StackRow>
  )
}
